import { Box, Divider, Stack, Typography } from '@mui/material';
import palette from '../../shared/theme/palette';
import Panel from '../../components/Panel';
import StatusChip from '../../components/StatusChip';
import { ReactComponent as BankIcon } from '../../assets/svg/bank.svg';
import { ReactComponent as CardIcon } from '../../assets/svg/card.svg';
import { getOrdinalSuffix } from '../../shared/util';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

export default function PaymentInfo({ person }) {
  const { t } = useTranslation();
  const relatedUser = person.relatedUser;
  const paymentStatus = person.balance?.[0]?.PaymentStatus;

  return (
    <Panel padding={2}>
      <Typography sx={{ fontWeight: 'bold', textTransform: 'uppercase', color: palette.grey[50], mb: 3 }}>{t('lease.payment.title')}</Typography>
      {!relatedUser || !relatedUser.paymentMethods || !relatedUser.paymentMethods?.length ? (
        <Box>
          <Typography>{t('lease.payment.empty')}</Typography>
        </Box>
      ) : (
        <Box>
          <Stack gap={3}>
            {relatedUser.paymentMethods
              ?.filter(pM => pM.status !== 'DELETED')
              .map(paymentMethod => (
                <Stack key={paymentMethod.id} direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" gap={1}>
                    {paymentMethod.type === 'CARD' ? <CardIcon /> : <BankIcon />}
                    <Box>
                      <Typography color="grey.900" sx={{ fontWeight: 600 }}>
                        {paymentMethod.cardType}
                      </Typography>
                      <Typography variant="caption" color="grey.600">
                        {`${paymentMethod.type === 'BANK' ? 'Account' : paymentMethod.isDebitCard ? 'Debit' : 'Credit'}`}
                        <Box
                          component="span"
                          sx={{ pl: 1 }}
                        >{`• • • • • ${paymentMethod.type === 'BANK' ? paymentMethod.bankAccountNo : paymentMethod.lastFourDigits}`}</Box>
                      </Typography>
                    </Box>
                  </Stack>
                  {paymentMethod.default && <StatusChip tone="info" label={t('common.default')} />}
                </Stack>
              ))}
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography color="grey.500">{t('lease.payment.autoPay')}</Typography>
            <StatusChip
              tone={relatedUser.autoPay?.isActive ? 'success' : 'info'}
              label={relatedUser.autoPay?.isActive ? t('lease.payment.on') : t('lease.payment.off')}
            />
          </Stack>
          {relatedUser.autoPay?.isActive && (
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography color="grey.500">{t('lease.payment.autoPayDate')}</Typography>
              <Typography color="grey.800">{`${getOrdinalSuffix(relatedUser.autoPay?.dayOfMonth)} of the Month`}</Typography>
            </Stack>
          )}
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography color="grey.500">{t('lease.payment.status')}</Typography>
            <StatusChip
              tone={paymentStatus === 'Accept' ? 'success' : paymentStatus === 'Certified Funds Only' ? 'warning' : 'info'}
              label={
                paymentStatus === 'Accept'
                  ? t('lease.payment.enabled')
                  : paymentStatus === 'Certified Funds Only'
                    ? t('lease.payment.certifiedFunds')
                    : t('lease.payment.disabled')
              }
            />
          </Stack>
          {!(paymentStatus === 'Accept' || paymentStatus === 'Certified Funds Only') && (
            <Stack direction="row" alignItems="center" gap={1} sx={{ pt: 1.5 }}>
              <InfoOutlinedIcon sx={{ color: palette.grey[40] }} />
              <Typography variant="caption" color="grey.600">
                {t('lease.payment.description')}
              </Typography>
            </Stack>
          )}
        </Box>
      )}
    </Panel>
  );
}
